/**
 * Appendix A.1 — RNG service. mulberry32, 32-bit unsigned state.
 * Any deviation from this implementation is a determinism-breaking bug.
 */

export class RngStream {
  private state: number; // uint32

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Exact mulberry32 — do not reorder, do not substitute operators. */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  next_u32(): number {
    return (this.next() * 4294967296) >>> 0;
  }

  next_int(n: number): number {
    return Math.floor(this.next() * n);
  }

  next_range(a: number, b: number): number {
    return a + this.next() * (b - a);
  }

  pick<T>(arr: readonly T[]): T {
    return arr[this.next_int(arr.length)];
  }

  /** Uniform point in a disc of radius r, area-correct. */
  next_in_disc(r: number, out: { x: number; y: number }): void {
    const a = this.next() * Math.PI * 2;
    const d = Math.sqrt(this.next()) * r;
    out.x = Math.cos(a) * d;
    out.y = Math.sin(a) * d;
  }

  getState(): number {
    return this.state >>> 0;
  }

  setState(s: number): void {
    this.state = s >>> 0;
  }
}

/** FNV-1a 32-bit over ASCII. Used for stream derivation and content hashing. */
export function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i) & 0xff;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Appendix A.1 stream registry. Gameplay streams never consumed by presentation. */
export const STREAM_NAMES = ['spawn', 'loot', 'ai', 'crit', 'vfx', 'audio', 'anomaly'] as const;
export type StreamName = (typeof STREAM_NAMES)[number];

/** Gameplay streams — replay-identity relevant. `anomaly` joined with AMD-51. */
export const GAMEPLAY_STREAMS: readonly StreamName[] = ['spawn', 'loot', 'ai', 'crit', 'anomaly'];

export type RngBank = Record<StreamName, RngStream>;

/** stream_seed = (run_seed ^ fnv1a32(stream_name)) >>> 0 */
export function deriveStreamSeed(runSeed: number, name: StreamName): number {
  return (runSeed ^ fnv1a32(name)) >>> 0;
}

export function createRngBank(runSeed: number): RngBank {
  const bank = {} as RngBank;
  for (const name of STREAM_NAMES) {
    bank[name] = new RngStream(deriveStreamSeed(runSeed, name));
  }
  return bank;
}

export function snapshotRngBank(bank: RngBank): Record<StreamName, number> {
  const out = {} as Record<StreamName, number>;
  for (const name of STREAM_NAMES) out[name] = bank[name].getState();
  return out;
}

export function restoreRngBank(bank: RngBank, states: Record<StreamName, number>): void {
  for (const name of STREAM_NAMES) {
    if (typeof states[name] === 'number') bank[name].setState(states[name]);
  }
}

/** Parse a user-entered seed: decimal canonical, `LA1:<hex>` share strings normalized (§13.7 OQ-13.2). */
export function parseSeed(input: string): number | null {
  const s = input.trim();
  if (s === '') return null;
  const share = /^LA1:([0-9a-fA-F]+)(?::|$)/.exec(s);
  if (share) {
    const v = parseInt(share[1], 16);
    return Number.isFinite(v) ? v >>> 0 : null;
  }
  if (/^0[xX][0-9a-fA-F]+$/.test(s)) return parseInt(s.slice(2), 16) >>> 0;
  if (!/^\d+$/.test(s)) return null;
  const v = Number(s);
  if (!Number.isFinite(v) || v < 0 || v > 0xffffffff) return null;
  return v >>> 0;
}
